// ISO date helpers (YYYY-MM-DD) using local calendar dates — avoids UTC offset bugs.

export function isoDateToLocalDate(iso: string): Date | undefined {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return undefined
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function localDateToIsoDate(date: Date | undefined): string {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Normalize any date/datetime string to a local ISO date (YYYY-MM-DD).
 * Accepts plain dates ("2026-07-17") as-is, and converts full datetime
 * strings (e.g. "2026-07-16T17:00:00.000000Z") to the matching local
 * calendar date — avoids the UTC offset shifting the day.
 */
export function normalizeToIsoDate(value: string | null | undefined): string {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return localDateToIsoDate(date)
}

export function formatIsoDateDisplay(iso: string): string {
  const date = isoDateToLocalDate(normalizeToIsoDate(iso))
  if (!date) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

/** Parse dd/mm/yyyy (also accepts - or . separators). Returns null when invalid. */
export function parseDisplayDateToIso(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return ''

  const match = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
  if (!match) return null

  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null

  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }

  return localDateToIsoDate(date)
}

export function todayIsoDate(): string {
  return localDateToIsoDate(new Date())
}
